// components/CreateGroupCallModal.js
import {
  Avatar,
  Box,
  Button,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { ReactComponent as VideoCallFilled } from "../../../Assets/img/icons/video-call-figma.svg";
import { sendGroupCallInvitaion } from "../../../Redux/Reducers/ContactReducer";
import { fetchRTCToken, generateUID } from "../../../utils/agora-utils";

const MAX_AVATARS = 4;

const CreateGroupCallModal = ({
  open,
  onClose,
  onStartCall,
  userId,
  selectedFolder,
}) => {
  // console.log("selectedFolder in CreateGroupCallModal:", selectedFolder);
  const [meetingName, setMeetingName] = useState("");
  const dispatch = useDispatch();
  const folderMembers = Array.isArray(selectedFolder?.members)
    ? selectedFolder.members
    : [];

  const callParticipants = folderMembers
    .map((m) => m?.contact_ref_id ?? m?.contactRefId ?? m?.contact_ref ?? null)
    .filter(Boolean);

  const verifiedContacts = folderMembers.filter((m) => !!m.is_verified);
  const nonVerifiedContacts = folderMembers.filter((m) => !m.is_verified);

  // Render avatars with "+N" overflow
  const renderAvatars = (memberList) => {
    const contactsToRender = Array.isArray(memberList) ? memberList : [];
    const displayContacts = contactsToRender.slice(0, MAX_AVATARS);
    const extraCount = contactsToRender.length - MAX_AVATARS;
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        {displayContacts.map((contact, idx) => {
          const key = contact?.contact_ref_id ?? contact?.id ?? idx;
          const src = contact?.profile_url ?? contact?.profileUrl ?? "";
          const alt = `${contact?.first_name ?? contact?.firstName ?? ""} ${
            contact?.last_name ?? contact?.lastName ?? ""
          }`.trim();
          return (
            <Avatar
              key={key}
              src={src}
              alt={alt}
              sx={{
                border: "2px solid #fff",
                marginLeft: idx === 0 ? 0 : "-15px",
                width: 48,
                height: 48,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            />
          );
        })}
        {extraCount > 0 && (
          <Avatar
            sx={{
              bgcolor: "#1976d2",
              color: "#fff",
              marginLeft: "-15px",
              width: 48,
              height: 48,
              fontWeight: 600,
            }}
          >
            {`+${extraCount}`}
          </Avatar>
        )}
      </Box>
    );
  };

  const handleCall = async () => {
    if (!meetingName) {
      alert("Please enter a meeting name.");
      return;
    }

    if (!callParticipants || callParticipants.length === 0) {
      alert("No participants in selected folder to call.");
      return;
    }

    try {
      // Generate UID for caller
      const uid = generateUID(userId);

      // console.log("handleCall - Generated UID:", uid);

      // Fetch RTC token
      const rtcTokenResult = await fetchRTCToken(meetingName, uid);
      const rtcToken =
        typeof rtcTokenResult === "string"
          ? rtcTokenResult
          : rtcTokenResult?.rtcToken ?? rtcTokenResult?.token ?? null;
      if (rtcTokenResult?.statusCode == 200) {
        const invitePayload = {
          meeting_name: meetingName,
          participants: callParticipants,
          rtc_token: rtcToken,
          user_id: uid,
        };

        const inviteResponse = await dispatch(
          sendGroupCallInvitaion(invitePayload)
        );
        if (inviteResponse?.payload?.data?.statusCode === 200) {
          onStartCall({
            meetingName,
            participants: callParticipants,
            rtcToken,
            uid,
          });
          onClose();
          // alert("Failed to send invitations. Please try again.");
        }
      } else {
        throw new Error("Failed to get RTC token");
      }
    } catch (error) {
      console.error("handleCall error:", error);
      // alert("Failed to start call. Please try again.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper style={{ width: "80%", maxWidth: "600px", padding: "24px" }}>
        <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <VideoCallFilled className="pointer figma-icon" />
          <Typography variant="h6" gutterBottom>
            Create Group Call
          </Typography>
        </Box>

        <Typography
          variant="subtitle1"
          style={{ marginTop: "20px", textAlign: "center" }}
        >
          {selectedFolder?.folder_name ?? "Selected Contacts"} (
          {folderMembers.length} Contacts)
        </Typography>

        <Box sx={{ margin: "20px 0", textAlign: "center" }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {verifiedContacts.length >= 1 ? "Verified Users" : ""}
          </Typography>
          {renderAvatars(verifiedContacts)}
        </Box>

        <Box sx={{ marginBottom: "20px", textAlign: "center" }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {nonVerifiedContacts.length >= 1 ? "Non-Verified Users" : ""}
          </Typography>
          {renderAvatars(nonVerifiedContacts)}
        </Box>

        <TextField
          fullWidth
          label="Type Meeting/Video Call name"
          value={meetingName}
          onChange={(e) => setMeetingName(e.target.value)}
          margin="normal"
        />

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCall}
            disabled={!meetingName || folderMembers.length === 0}
            sx={{ width: "120px", fontWeight: 600 }}
          >
            CALL
          </Button>

          {/* <Button variant="outlined" onClick={onClose} sx={{ width: "120px" }}>
            CANCEL
          </Button> */}
        </Box>
      </Paper>
    </Modal>
  );
};

export default CreateGroupCallModal;
